import type {
  ManagerApprovalsList,
  ManagerBatchDecideBody,
  ManagerDashboard,
  ManagerDecisionResult,
  ManagerPendingApproval,
  ManagerSyncVacationsResult,
  ManagerTeamIndicators,
  ManagerTeamIndicatorsQuery,
  ManagerTeamList,
  ManagerTeamQuery,
  ManagerVacationCalendar,
  ManagerVacationCalendarQuery,
} from "@evolua/api-client";
import { EvoluaApiError } from "@evolua/api-client";

/**
 * Fila do gestor — fixtures demo (FASE-06-T42/T44/T47).
 * Estado mutável em memória para aprovar/reprovar no modo demo.
 */

/** Simula saldo STALE até sync (T47). */
let demoVacationStale = true;

let queue: ManagerPendingApproval[] = [
  {
    id: "11111111-aaaa-4aaa-8aaa-111111111111",
    type: "VACATION",
    label: "Férias 2026-09-10–2026-09-19 (10 dias)",
    requesterName: "Ana Silva",
    employeeId: "11111111-1111-4111-8111-111111111111",
    status: "ACTIVE",
    dueAt: "2026-08-18T18:00:00.000Z",
    href: "/aprovacoes",
  },
  {
    id: "22222222-bbbb-4bbb-8bbb-222222222222",
    type: "VACATION",
    label: "Férias 2026-10-01–2026-10-05 (5 dias)",
    requesterName: "Ana Silva",
    employeeId: "11111111-1111-4111-8111-111111111111",
    status: "ACTIVE",
    dueAt: "2026-08-20T18:00:00.000Z",
    href: "/aprovacoes",
  },
];

export function getDemoManagerDashboard(): ManagerDashboard {
  return {
    managerEmployeeId: "22222222-2222-4222-8222-222222222222",
    teamSize: 2,
    pendingCount: queue.length,
    pendingApprovals: queue.map((item) => ({ ...item })),
    absences: [
      {
        employeeId: "33333333-3333-4333-8333-333333333333",
        fullName: "Carla Dias",
        status: "ON_LEAVE",
        startDate: "2026-08-01",
        endDate: "2026-08-31",
      },
    ],
    birthdays: [
      {
        employeeId: "11111111-1111-4111-8111-111111111111",
        fullName: "Ana Silva",
        birthMonthDay: "08-15",
        daysUntil: 3,
      },
      {
        employeeId: "33333333-3333-4333-8333-333333333333",
        fullName: "Carla Dias",
        birthMonthDay: "08-20",
        daysUntil: 8,
      },
    ],
    alerts: [
      {
        id: "alert-ana-stale",
        severity: "warning",
        message: "Saldo de férias de Ana pode estar desatualizado (>7 dias).",
        employeeId: "11111111-1111-4111-8111-111111111111",
        href: "/equipe/11111111-1111-4111-8111-111111111111",
      },
    ],
    links: {
      approvals: "/aprovacoes",
      team: "/equipe",
      vacationCalendar: "/calendario-ferias",
      teamIndicators: "/indicadores-equipe",
    },
    syncedAt: new Date().toISOString(),
  };
}

export function listDemoManagerApprovals(): ManagerApprovalsList {
  return { data: queue.map((item) => ({ ...item })), total: queue.length };
}

export function approveDemoManagerApproval(
  id: string,
  body: { comment?: string; force?: boolean } = {},
): ManagerDecisionResult {
  if (demoVacationStale) {
    throw new EvoluaApiError({
      type: "https://evolua.hcm/errors/vacation-approve-stale",
      title: "Dado desatualizado",
      status: 409,
      detail:
        "Dado de vacations desatualizado (Atualizado há 5 h). Sincronize antes de continuar. Use Sincronizar saldo e tente aprovar de novo.",
      code: "VACATION_APPROVE_STALE",
    });
  }
  const found = queue.find((item) => item.id === id);
  if (!found) {
    throw new Error("Solicitação não encontrada");
  }
  // Pedido de out/2026 sobrepõe Ana+Carla no calendário seed (T45).
  if (found.label.includes("2026-10") && !body.force) {
    throw new EvoluaApiError({
      type: "https://evolua.hcm/errors/manager-vacation-coverage",
      title: "Cobertura da equipe",
      status: 409,
      detail:
        "Aprovar este período deixaria a equipe abaixo de 50% presente em dias sobrepostos. Confirme com force=true se quiser prosseguir.",
      code: "MANAGER_VACATION_COVERAGE",
    });
  }
  queue = queue.filter((item) => item.id !== id);
  return { id, status: "APPROVED", notificationId: `notif-${id}` };
}

export function syncDemoManagerVacations(): ManagerSyncVacationsResult {
  demoVacationStale = false;
  return {
    ok: true,
    label: "Atualizado agora",
    lastSuccessAt: new Date().toISOString(),
  };
}

export function rejectDemoManagerApproval(
  id: string,
  body: { comment: string },
): ManagerDecisionResult {
  if (!body.comment.trim()) {
    throw new Error("Justificativa obrigatória na reprovação");
  }
  const found = queue.find((item) => item.id === id);
  if (!found) {
    throw new Error("Solicitação não encontrada");
  }
  queue = queue.filter((item) => item.id !== id);
  return { id, status: "REJECTED", notificationId: `notif-${id}` };
}

export function batchDemoManagerApprovals(body: ManagerBatchDecideBody): {
  results: ManagerDecisionResult[];
} {
  const results: ManagerDecisionResult[] = [];
  for (const item of body.items) {
    if (item.decision === "REJECT") {
      results.push(rejectDemoManagerApproval(item.id, { comment: item.comment ?? "" }));
    } else {
      results.push(
        approveDemoManagerApproval(item.id, {
          ...(item.comment ? { comment: item.comment } : {}),
          ...(item.force ? { force: true } : {}),
        }),
      );
    }
  }
  return { results };
}

export function getDemoManagerVacationCalendar(
  query: ManagerVacationCalendarQuery = {},
): ManagerVacationCalendar {
  const from = query.from ?? "2026-10-01";
  const minPresentPercent = query.minPresentPercent ?? 50;
  const events: ManagerVacationCalendar["events"] = [
    {
      id: "cal-ana",
      employeeId: "11111111-1111-4111-8111-111111111111",
      employeeName: "Ana Silva",
      startDate: "2026-10-05",
      endDate: "2026-10-16",
      status: "APPROVED",
      overlaps: true,
    },
    {
      id: "cal-carla",
      employeeId: "33333333-3333-4333-8333-333333333333",
      employeeName: "Carla Dias",
      startDate: "2026-10-08",
      endDate: "2026-10-20",
      status: "APPROVED",
      overlaps: true,
    },
  ];
  const days: ManagerVacationCalendar["days"] = [];
  for (let d = 1; d <= 31; d += 1) {
    const date = `2026-10-${String(d).padStart(2, "0")}`;
    const onVacation = events.filter(
      (e) => e.startDate <= date && date <= e.endDate,
    ).length;
    const presentCount = Math.max(0, 2 - onVacation);
    const presentPercent = Math.round((presentCount / 2) * 1000) / 10;
    days.push({
      date,
      onVacationCount: onVacation,
      presentCount,
      presentPercent,
      belowMinPresence: presentPercent < minPresentPercent,
    });
  }
  return {
    from,
    to: "2026-10-31",
    view: query.view ?? "month",
    teamSize: 2,
    minPresentPercent,
    events,
    overlaps: [
      {
        aId: "cal-ana",
        bId: "cal-carla",
        overlapStart: "2026-10-08",
        overlapEnd: "2026-10-16",
      },
    ],
    days,
    hasCoverageAlert: true,
  };
}

const DEMO_TEAM: ManagerTeamList = {
  data: [
    {
      id: "11111111-1111-4111-8111-111111111111",
      fullName: "Ana Silva",
      displayName: "Ana",
      status: "ACTIVE",
      presence: "ACTIVE",
      departmentName: "RH",
      positionName: "Analista",
      managerId: "22222222-2222-4222-8222-222222222222",
      relation: "DIRECT",
      hireDate: "2020-01-01",
      syncedAt: new Date().toISOString(),
      salaryAmount: null,
    },
    {
      id: "33333333-3333-4333-8333-333333333333",
      fullName: "Carla Dias",
      displayName: "Carla",
      status: "ON_LEAVE",
      presence: "ON_LEAVE",
      departmentName: "Operações",
      positionName: "Analista",
      managerId: "22222222-2222-4222-8222-222222222222",
      relation: "DIRECT",
      hireDate: "2020-01-03",
      syncedAt: new Date().toISOString(),
      salaryAmount: null,
    },
  ],
  total: 2,
  directCount: 2,
  indirectCount: 0,
};

export function listDemoManagerTeam(query: ManagerTeamQuery = {}): ManagerTeamList {
  let data = DEMO_TEAM.data.map((m) => ({ ...m }));
  if (query.status) data = data.filter((m) => m.status === query.status);
  if (query.relation === "DIRECT") data = data.filter((m) => m.relation === "DIRECT");
  if (query.relation === "INDIRECT") {
    data = data.filter((m) => m.relation === "INDIRECT");
  }
  if (query.q?.trim()) {
    const q = query.q.trim().toLowerCase();
    data = data.filter(
      (m) =>
        m.fullName.toLowerCase().includes(q) ||
        m.departmentName.toLowerCase().includes(q),
    );
  }
  return {
    data,
    total: data.length,
    directCount: data.filter((m) => m.relation === "DIRECT").length,
    indirectCount: data.filter((m) => m.relation === "INDIRECT").length,
  };
}

export function getDemoManagerTeamIndicators(
  query: ManagerTeamIndicatorsQuery = {},
): ManagerTeamIndicators {
  const period = query.period ?? "month";
  const from =
    query.from ??
    (period === "year"
      ? "2026-01-01"
      : period === "quarter"
        ? "2026-07-01"
        : "2026-08-01");
  const to =
    period === "year" ? "2026-12-31" : period === "quarter" ? "2026-09-30" : "2026-08-31";
  return {
    from,
    to,
    period,
    teamSize: 2,
    hires: period === "month" || period === "quarter" || period === "year" ? 1 : 0,
    terminations: 1,
    absenteeismPercent: 50,
    absenceDays: 31,
    availableWorkDays: 62,
    absencesInPeriod: [
      {
        employeeId: "33333333-3333-4333-8333-333333333333",
        fullName: "Carla Dias",
        status: "ON_LEAVE",
        startDate: "2026-08-01",
        endDate: "2026-08-31",
        daysInPeriod: 31,
      },
    ],
    vacationsExpiring: [
      {
        employeeId: "11111111-1111-4111-8111-111111111111",
        fullName: "Ana Silva",
        balanceDays: 12,
        limitDate: "2026-08-28",
      },
    ],
    formulas: {
      headcount: "Colaboradores da subárvore com status ≠ TERMINATED.",
      hires: "hireDate ∈ [from, to].",
      terminations: "terminationDate ∈ [from, to].",
      absenteeism: "absenceDays / availableWorkDays × 100.",
      vacationsExpiring: "limitDate ∈ [from, to] com saldo > 0.",
    },
    previous: {
      teamSize: 2,
      hires: 0,
      terminations: 0,
      absenteeismPercent: 0,
    },
  };
}
